export interface ReleaseNote {
  version: string;
  date: string;
  title?: string;
  changes: string[];
}

export const releaseNotes: ReleaseNote[] = [
  // ═══════════════════════════════════════════════════════════════
  // 📋 TEMPLATE FOR NEW RELEASES:
  // ═══════════════════════════════════════════════════════════════
  // {
  //   version: 'v1.2.0',
  //   date: 'January 15, 2025',
  //   title: 'Your Release Title',
  //   changes: [
  //     'Feature description',
  //     'Bug fix description',
  //     'Another improvement',
  //   ],
  // },

  // ═══════════════════════════════════════════════════════════════
  // 🎨 STYLING NOTES:
  // ═══════════════════════════════════════════════════════════════
  // Version Chip Colors:
  // - 🟢 Green: Titles containing "major" (case insensitive)
  // - 🔵 Blue: All other releases
  //
  // Change Icons (automatically determined by text content):
  // - 🟢 Green Star: Changes containing "new", "added", or "major"
  // - 🔵 Blue Release: All other changes (default)
  // - 🟠 Orange Bug: Changes containing "bug fix", "fixed", "resolve", or "invalid"
  //
  // {
  //   version: 'v1.1.1',
  //   date: 'Sep 14, 2025',
  //   title: 'Small update',
  //   changes: ['tbh nothing really', 'bug fixes: removed Grand Mother Silk from the Citadel', 'added cats'],
  // },

  {
    version: 'v1.7.0',
    date: 'March 6, 2026',
    title: 'Major Update: PMS Integration (Rent Manager)',
    changes: [
      'New PMS Export: export billing data as a Rent Manager-compatible CSV directly from the bill breakdown',
      'New PMS Settings card on the property Billing tab to configure Rent Manager connection details',
      'Added per-unit Rent Manager name mapping for accurate charge exports',
    ],
  },
  {
    version: 'v1.6.2',
    date: 'February 23, 2026',
    title: 'Notification Banner Fixes',
    changes: [
      'Fixed payment warning banner overlapping the DELETE button on the property page',
      'Fixed notification banners stacking on top of each other when both are visible',
    ],
  },
  {
    version: 'v1.6.1',
    date: 'February 23, 2026',
    title: 'Invoice Email Tracking',
    changes: [
      'New "Invoice Sent" column in bill details shows when each invoice was emailed, in the property\'s timezone',
      'Added warning when re-sending invoices that have already been emailed',
      'New "Invoices Emailed" column on the Billing tab shows email sent count per bill',
    ],
  },
  {
    version: 'v1.6.0',
    date: 'February 18, 2026',
    title: 'Major Update: Email Invoices',
    changes: [
      'New ability to email SSW invoices directly to tenants with PDF attachments',
      'Added selective email sending — use checkboxes to choose which tenants receive SSW invoices by email',
      'New confirmation dialog shows a table of Unit, Full Name, and Email before sending',
    ],
  },
  {
    version: 'v1.5.6',
    date: 'February 9, 2026',
    title: 'Invoice Download Naming',
    changes: ['Batch SSW invoice downloads now use descriptive filenames'],
  },
  {
    version: 'v1.5.5',
    date: 'February 5, 2026',
    title: 'Team Role Safety Improvements',
    changes: [
      'Added confirmation dialog when owners downgrade their own role',
      'Prevents the last owner from downgrading themselves with a helpful error message',
    ],
  },
  {
    version: 'v1.5.4',
    date: 'February 4, 2026',
    title: 'Bill Editing Improvements',
    changes: [
      'Added ability to add, edit, and remove fees when editing a bill',
      'Fixed bug where editing a bill would cause invoices to show $0',
    ],
  },

  {
    version: 'v1.5.3',
    date: 'February 4, 2026',
    title: 'Invoice Due Date Improvements',
    changes: [
      'Various minor improvements to invoice due date handling',
      'Due date displays "—" when no invoice exists',
      'Fixed due date not persisting when reopening bill details',
    ],
  },
  {
    version: 'v1.5.2',
    date: 'February 3, 2026',
    title: 'Timezone support for Invoice Dates',
    changes: ['Fixed invoice dates to consistently use property timezone'],
  },
  {
    version: 'v1.5.1',
    date: 'February 2, 2026',
    title: 'Invoice Due Date Settings',
    changes: [
      'Added configurable "Days Until Due" for invoices, with default setting per property',
      'Renamed "Payment Instructions" to "Comments" for clarity and flexibility',
      'Fixed invoice generation failing for properties or units with non-ASCII characters in their names',
    ],
  },
  {
    version: 'v1.4.0',
    date: 'January 22, 2026',
    title: 'Major Update: SSW Invoices',
    changes: [
      'New SSW invoice generation with usage graphs and detailed line items',
      'Supports custom payment instructions and online payment links, with defaults per property',
      'Bug fixes: Resolved issues with invoice contact assignment, "Unit Name" column in Tenants table now sorts alphanumerically',
    ],
  },
  {
    version: 'v1.3.2',
    date: 'January 6, 2026',
    title: 'Update Link',
    changes: ['Update "Contact Support" link'],
  },
  {
    version: 'v1.3.1',
    date: 'December 9, 2025',
    title: 'Update Link',
    changes: ['Update email delivery troubleshooting link'],
  },
  {
    version: 'v1.3.0',
    date: 'December 8, 2025',
    title: 'Major Update: Account Roles & Permissions',
    changes: [
      'New role-based access control system with Owner, Admin, and Billing Manager roles for team members',
      'Added user permissions, checking access across the application',
      'Owner role has full access including team management and billing',
      'Admin role has full access, except inviting or editing Owners',
      'Billing Manager role grants access to billing features and payment management. They cannot invite/edit users, view usage data, or modify notification settings for others',
      'Added ability for Owners and Admins to delete team members (Admins cannot delete Owners)',
      'Added "You" badge next to the currently signed-in user in Team Members table',
      "All team members are included in the notifications table (even if they haven't reset password)",
      'Fixed: Resent invites used the description as the invite email memo',
    ],
  },
  {
    version: 'v1.2.3',
    date: 'November 3, 2025',
    title: 'Usage Export Fixes',
    changes: [
      'Fixed: Usage exports now include all selected days and display accurate values with proper formatting',
      'Fixed: Custom date range exports now return the complete selected date range',
    ],
  },
  {
    version: 'v1.2.2',
    date: 'Oct 29, 2025',
    title: 'Multi-Line Item Subscription Support',
    changes: [
      'Added support for displaying multiple line items in SimpleSUB Service Fee subscriptions',
      'New interactive tooltip shows itemized breakdown when hovering over subscription amounts',
    ],
  },
  {
    version: 'v1.2.1',
    date: 'Oct 23, 2025',
    title: 'Service Fee Enhancements',
    changes: [
      'Added support for SimpleSUB Service Fee subscriptions scheduled to start on a future date',
      'Improved warning message clarity for canceled Service Fee subscriptions',
    ],
  },
  {
    version: 'v1.2.0',
    date: 'Oct 21, 2025',
    title: 'Major Update: SimpleSUB Service Fee Management',
    changes: [
      'New SimpleSUB Service Fee subscription management in Account Settings with payment method setup',
      'Added payment warning banner to prompt users to add payment methods for uninterrupted service',
      'Account lockout protection for payment-related issues with self-service payment portal access',
      'New subscription status dashboard showing active subscriptions, billing dates, and payment details',
      'Organization name now displayed for all account types in Account Settings',
      'Default fee type changed from Split to Flat',
      'Fixed bug where unit and property usage exports included an extra day beyond the selected date range',
    ],
  },
  {
    version: 'v1.1.3',
    date: 'Oct 1, 2025',
    title: 'Usage Export Accuracy Fix',
    changes: [
      'Fixed bug where usage export spreadsheets showed different totals than usage graphs due to double-rounding',
    ],
  },
  {
    version: 'v1.1.2',
    date: 'Sep 8, 2025',
    title: 'Notification Settings Bug Fix',
    changes: [
      'Fixed bug where editing team member details (phone/description) would reset SMS notification preferences',
      'Improved Send Usage Report confirmation dialog styling',
    ],
  },
  {
    version: 'v1.1.1',
    date: 'Sep 5, 2025',
    title: 'UI Improvements & Validation',
    changes: [
      'Added Version History page with detailed changelogs',
      'Fixed duplicate headers and buttons in property creation flow',
      'Added automatic version checking for outdated webapp instances',
      'Provide redirect for invalid property IDs to home',
    ],
  },
  {
    version: 'v1.1.0',
    date: 'Sep 4, 2025',
    title: 'Major Update: Phone Numbers & Enhanced Billing',
    changes: [
      'Added phone number support for users and tenants',
      'Added alert configuration for users and tenants (device health, leaks, etc.)',
      'Added ability to include fees on bills for everyone (not just Stripe-enabled accounts)',
      'Bills show breakdown of water and fees per unit',
      'Spreadsheet exports include water, fees, and total cost breakdown',
      'Added "Add New Property" button on the home page',
      'Bug fixes: Invoice statuses refresh when selecting View Bill, Unit usage graphs show selected dates, Safari download issues resolved',
    ],
  },
  {
    version: 'v1.0.29',
    date: 'Jul 25, 2025',
    title: 'Outage Handling & User Experience',
    changes: [
      'New Maintenance/Outage page shown if databases are down',
      'Clear red error messages and success notifications for password reset',
      'Bug fixes: Selecting an action from Account menu now closes the menu',
    ],
  },
  {
    version: 'v1.0.28',
    date: 'Jul 21, 2025',
    title: 'Team Management & Settings',
    changes: [
      'New Feature: Invite and resend invites for team members',
      'New tabs layout in settings',
    ],
  },
  {
    version: 'v1.0.27',
    date: 'Jul 21, 2025',
    title: 'Major Update: Billing Flow Overhaul',
    changes: [
      'Simplified billing hamburger menu. Actions consolidated into "View Bill" popup',
      'New view in bill review screen',
      'Empty Bill and Tenant tables now prompt users to add data',
      'Bug fixes: Fees correctly included in totals, common areas hidden when adding fees, PDF downloads include all units',
    ],
  },
  {
    version: 'v1.0.26',
    date: 'Jun 16, 2025',
    title: 'Download & Billing Improvements',
    changes: ['Fixed mislabeled download buttons', 'Clearer button wording in billing flow'],
  },
  {
    version: 'v1.0.25',
    date: 'Jun 13, 2025',
    title: 'Invoice Contacts & Billing Accuracy',
    changes: [
      'First tenant added to a unit is automatically set as invoice contact',
      'Occupancy dates always visible when adding a tenant',
      'Fixed billing rounding issue',
      'UI cleanup: clearer labels and info icons, "CSV" renamed to "Spreadsheet"',
    ],
  },
  {
    version: 'v1.0.24',
    date: 'Jun 9, 2025',
    title: 'Stability Improvements',
    changes: ['Internal testing improvements for better stability and reliability'],
  },
];
